import { useCallback, useEffect, useRef, useState } from "react";
import { Fetch, OptionsI } from "../../utils/fetch";

interface ResponseI {
  data: Array<any> | any;
  loading: boolean;
}

interface FecthI {
  noMore: boolean;
  loadMore: boolean;
  page: number;
  keyword: string;
}

export interface ExtraOptionsI {
  time?: number;
  requestOptions?: OptionsI;
  extraParams?: string;
  limit?: number;
}

export const useSearchableData = (
  url: string,
  param: string,
  options: ExtraOptionsI = { time: 700, extraParams: "" },
) => {
  const { time, requestOptions, extraParams, limit } = options;
  const timeoutRef = useRef<any>(null);
  const mountedRef = useRef<boolean>(false);
  const [ response, setResponse ] = useState<ResponseI>({
    data    : [],
    loading : true,
  });

  const [ fetch, setFetch ] = useState<FecthI>({
    noMore   : false,
    loadMore : false,
    page     : 1,
    keyword  : "",
  });
  const { page, keyword, loadMore, noMore } = fetch;

  const setKeyword = useCallback((keyword: string) => setFetch((prev) => ({ ...prev, keyword, page: 1 })), []);

  const getNextPage = useCallback(() => !loadMore && !noMore && setFetch((prev) => ({ ...prev, page: prev.page + 1, loadMore: true })), [ loadMore, noMore ]);

  useEffect(() => {
    setResponse((prev) => ({ ...prev, loading: page === 1 }));
    mountedRef.current = true;
    const getData = () => {
      Fetch(
        "get",
        `${url}?limit=${limit || 20}&page=${page}${keyword ? `&${param}=${keyword}` : ""}${extraParams || ""}`,
        null,
        requestOptions,
      )
        .then(({ data = [] }: any) => {
          if (mountedRef.current) {
            setResponse((prev) => ({
              ...prev,
              data    : page === 1 ? data : [ ...prev.data, ...data ],
              loading : false,
            }));
            setFetch((prev) => ({
              ...prev,
              loadMore : false,
              noMore   : data.length < 20,
            }));
          }
        })
        .catch(() => {
          if (mountedRef.current) {
            setResponse({
              data    : [],
              loading : false,
            });

            setFetch((prev) => ({
              ...prev,
              loadMore : false,
              noMore   : true,
            }));
          }
        });
    };

    timeoutRef.current && clearInterval(timeoutRef.current);

    timeoutRef.current = setTimeout(() => getData(), time);

    return () => {
      mountedRef.current = false;
    };
  }, [ keyword, param, url, page, time, requestOptions, extraParams, limit ]);

  return {
    ...response,
    setKeyword,
    getNextPage,
    ...fetch,
  };
};
